import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { BASE_URL } from '../../config/config';
import { NgxSpinnerService } from "ngx-spinner";
import { Router } from '@angular/router';

export interface DashboardMetrics {
  active_stocks?: MetricData;
  consumed_stocks?: MetricData;
  missing_stocks?: MetricData;
  low_stocks?: MetricData;
  transit_stocks?: MetricData;
  expired_stocks?: MetricData;
  expiring_stocks?: MetricData;
  devices?: MetricData;
}

export interface MetricData {
  count: number;
  previous_count: number;
  percentage_change: number;
  trend: 'up' | 'down' | 'stable';
}

export interface DateFilter {
  value: string;
  label: string;
}

export interface Store {
  id: number;
  name: string;
}

export interface ConsumptionTrendData {
  labels: string[];
  data: number[];
  period: 'day' | 'week' | 'month';
}

export interface TopConsumedProduct {
  product_name: string;
  count: number;
}

export interface TopConsumedProductsData {
  products: TopConsumedProduct[];
  others_count: number;
  total_consumed: number;
}

export interface TopStoreActivity {
  store_name: string;
  count: number;
}

export interface TopStoresByActivityData {
  stores: TopStoreActivity[];
  total_count: number;
  activity_type: 'consumed' | 'missing';
}

export interface ConsumedProductType {
  product_type_name: string;
  count: number;
}

export interface MostConsumedProductTypeData {
  product_types: ConsumedProductType[];
  total_count: number;
  category_type: 'body_part' | 'implant';
}

export interface ExpiredSummaryData {
  labels: string[];
  data: number[];
  period: 'day' | 'week' | 'month';
}

export interface AboutToExpiryData {
  labels: string[];
  data: number[];
  period: 'day' | 'week' | 'month';
}

export interface ExpiredSummaryTableData {
  store_name: string;
  count: number;
}

export interface AboutToExpiryTableData {
  product_stock_id: number;
  product_name: string;
  product_code: string;
  batch_no: string;
  store_name: string;
  cabinet_name: string | null;
  expiry_date: string;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardMetricsService {
  private apiUrl = `${BASE_URL.URL}/dashboard`;

  constructor(
    private http: HttpClient,
    private spinnerService: NgxSpinnerService,
    private route: Router
  ) {}

  /**
   * Get dashboard metrics with dynamic filtering
   */
  getMetrics(params: {
    store_id: number;
    date_filter: string;
    custom_from_date?: string;
    custom_to_date?: string;
    counts: string;
  }): Observable<{success: boolean; data: DashboardMetrics; date_range: any}> {
    this.spinnerService.show();
    let token = localStorage.getItem('token');

    return this.http.post<{success: boolean; data: DashboardMetrics; date_range: any}>(
      `${this.apiUrl}/metrics`,
      params,
      { 
        headers: {
          'Authorization': 'Bearer ' + token
        }
      }
    ).pipe(
      map((res: any) => {
        return res;
      }),
      tap(err => this.spinnerService.hide()),
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get available date filters
   */
  getDateFilters(): Observable<{success: boolean; data: DateFilter[]}> {
    this.spinnerService.show();
    let token = localStorage.getItem('token');

    return this.http.get<{success: boolean; data: DateFilter[]}>(
      `${this.apiUrl}/date-filters`,
      { 
        headers: {
          'Authorization': 'Bearer ' + token
        }
      }
    ).pipe(
      map((res: any) => {
        return res;
      }),
      tap(err => this.spinnerService.hide()),
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get user stores
   */
  getUserStores(): Observable<{success: boolean; data: Store[]}> {
    this.spinnerService.show();
    let token = localStorage.getItem('token');
    let userId = localStorage.getItem('userId');

    return this.http.get<{success: boolean; data: Store[]}>(
      `${this.apiUrl}/user-stores?user_id=${userId}`,
      { 
        headers: {
          'Authorization': 'Bearer ' + token
        }
      }
    ).pipe(
      map((res: any) => {
        return res;
      }),
      tap(err => this.spinnerService.hide()),
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get consumption trend data
   */
  getConsumptionTrend(params: {
    store_id: number;
    date_filter: string;
    custom_from_date?: string;
    custom_to_date?: string;
  }): Observable<{success: boolean; data: ConsumptionTrendData}> {
    this.spinnerService.show();
    let token = localStorage.getItem('token');

    return this.http.post<{success: boolean; data: ConsumptionTrendData}>(
      `${this.apiUrl}/consumption-trend`,
      params,
      { 
        headers: {
          'Authorization': 'Bearer ' + token
        }
      }
    ).pipe(
      map((res: any) => {
        return res;
      }),
      tap(err => this.spinnerService.hide()),
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get top consumed products data
   */
  getTopConsumedProducts(params: {
    store_id: number;
    date_filter: string;
    custom_from_date?: string;
    custom_to_date?: string;
  }): Observable<{success: boolean; data: TopConsumedProductsData}> {
    this.spinnerService.show();
    let token = localStorage.getItem('token');

    return this.http.post<{success: boolean; data: TopConsumedProductsData}>(
      `${this.apiUrl}/top-consumed-products`,
      params,
      { 
        headers: {
          'Authorization': 'Bearer ' + token
        }
      }
    ).pipe(
      map((res: any) => {
        return res;
      }),
      tap(err => this.spinnerService.hide()),
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get top stores by activity data
   */
  getTopStoresByActivity(params: {
    store_id: number;
    date_filter: string;
    activity_type: 'consumed' | 'missing';
    custom_from_date?: string;
    custom_to_date?: string;
  }): Observable<{success: boolean; data: TopStoresByActivityData}> {
    this.spinnerService.show();
    let token = localStorage.getItem('token');

    return this.http.post<{success: boolean; data: TopStoresByActivityData}>(
      `${this.apiUrl}/top-stores-by-activity`,
      params,
      { 
        headers: {
          'Authorization': 'Bearer ' + token
        }
      }
    ).pipe(
      map((res: any) => {
        return res;
      }),
      tap(err => this.spinnerService.hide()),
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get most consumed product type data
   */
  getMostConsumedProductType(params: {
    store_id: number;
    date_filter: string;
    category_type: 'body_part' | 'implant';
    custom_from_date?: string;
    custom_to_date?: string;
  }): Observable<{success: boolean; data: MostConsumedProductTypeData}> {
    this.spinnerService.show();
    let token = localStorage.getItem('token');

    return this.http.post<{success: boolean; data: MostConsumedProductTypeData}>(
      `${this.apiUrl}/most-consumed-product-type`,
      params,
      { 
        headers: {
          'Authorization': 'Bearer ' + token
        }
      }
    ).pipe(
      map((res: any) => {
        return res;
      }),
      tap(() => this.spinnerService.hide()),
      catchError((error: HttpErrorResponse) => {
        this.spinnerService.hide();
        console.error('Error in getMostConsumedProductType:', error);
        
        // Handle specific error types
        if (error.status === 0) {
          // Network error or connection refused
          return throwError(() => new Error('Network error: Unable to connect to server'));
        } else if (error.status === 401) {
          // Unauthorized - redirect to login
          this.route.navigate(['/login']);
          return throwError(() => new Error('Authentication required'));
        } else if (error.status === 403) {
          return throwError(() => new Error('Access denied to this store'));
        } else {
          return throwError(() => new Error(error.error?.message || 'Error retrieving most consumed product type data'));
        }
      })
    );
  }

  /**
   * Get expired summary data
   */
  getExpiredSummary(params: {
    store_id: number;
    date_filter: string;
    custom_from_date?: string;
    custom_to_date?: string;
  }): Observable<{success: boolean; data: ExpiredSummaryTableData[]}> {
    this.spinnerService.show();
    let token = localStorage.getItem('token');

    return this.http.post<{success: boolean; data: ExpiredSummaryTableData[]}>(
      `${this.apiUrl}/expired-summary`,
      params,
      { 
        headers: {
          'Authorization': 'Bearer ' + token
        }
      }
    ).pipe(
      map((res: any) => {
        return res;
      }),
      tap(() => this.spinnerService.hide()),
      catchError((error: HttpErrorResponse) => {
        this.spinnerService.hide();
        console.error('Error in getExpiredSummary:', error);
        
        // Handle specific error types
        if (error.status === 0) {
          return throwError(() => new Error('Network error: Unable to connect to server'));
        } else if (error.status === 401) {
          this.route.navigate(['/login']);
          return throwError(() => new Error('Authentication required'));
        } else if (error.status === 403) {
          return throwError(() => new Error('Access denied to this store'));
        } else {
          return throwError(() => new Error(error.error?.message || 'Error retrieving expired summary data'));
        }
      })
    );
  }

  /**
   * Get about to expiry data
   */
  getAboutToExpiry(params: {
    store_id: number;
    date_filter: string;
    custom_from_date?: string;
    custom_to_date?: string;
  }): Observable<{success: boolean; data: AboutToExpiryTableData[]}> {
    this.spinnerService.show();
    let token = localStorage.getItem('token');

    return this.http.post<{success: boolean; data: AboutToExpiryTableData[]}>(
      `${this.apiUrl}/about-to-expiry`,
      params,
      { 
        headers: {
          'Authorization': 'Bearer ' + token
        }
      }
    ).pipe(
      map((res: any) => {
        return res;
      }),
      tap(() => this.spinnerService.hide()),
      catchError((error: HttpErrorResponse) => {
        this.spinnerService.hide();
        console.error('Error in getAboutToExpiry:', error);
        
        // Handle specific error types
        if (error.status === 0) {
          return throwError(() => new Error('Network error: Unable to connect to server'));
        } else if (error.status === 401) {
          this.route.navigate(['/login']);
          return throwError(() => new Error('Authentication required'));
        } else if (error.status === 403) {
          return throwError(() => new Error('Access denied to this store'));
        } else {
          return throwError(() => new Error(error.error?.message || 'Error retrieving about to expiry data'));
        }
      })
    );
  }

  /**
   * Get low stock data
   */
  getLowStock(params: {
    store_id: number;
    date_filter: string;
    custom_from_date?: string;
    custom_to_date?: string;
  }): Observable<{success: boolean; data: any[]}> {
    this.spinnerService.show();
    let token = localStorage.getItem('token');

    return this.http.post<{success: boolean; data: any[]}>(
      `${this.apiUrl}/low-stock`,
      params,
      { 
        headers: {
          'Authorization': 'Bearer ' + token
        }
      }
    ).pipe(
      map((res: any) => {
        return res;
      }),
      tap(() => this.spinnerService.hide()),
      catchError((error: HttpErrorResponse) => {
        this.spinnerService.hide();
        console.error('Error in getLowStock:', error);
        if (error.status === 0) {
          return throwError(() => new Error('Network error: Unable to connect to server'));
        } else if (error.status === 401) {
          this.route.navigate(['/login']);
          return throwError(() => new Error('Authentication required'));
        } else if (error.status === 403) {
          return throwError(() => new Error('Access denied to this store'));
        } else {
          return throwError(() => new Error(error.error?.message || 'Error retrieving low stock data'));
        }
      })
    );
  }

  /**
   * Handle errors consistently across all methods
   */
  private handleError(error: HttpErrorResponse) {
    this.spinnerService.hide();
    console.error('Service error:', error);
    
    // Handle specific error types
    if (error.status === 0) {
      // Network error or connection refused
      return throwError(() => new Error('Network error: Unable to connect to server'));
    } else if (error.status === 401) {
      // Unauthorized - redirect to login
      this.route.navigate(['/login']);
      return throwError(() => new Error('Authentication required'));
    } else if (error.status === 403) {
      return throwError(() => new Error('Access denied'));
    } else {
      return throwError(() => new Error(error.error?.message || 'An error occurred'));
    }
  }
} 